import { Controller } from "@hotwired/stimulus"

// Minimal scaffolding for the funnel visual builder. Selection + the
// "saved" chip are real; drag-drop from the palette is stubbed (logs
// only) — the canvas needs server-side step creation before it's worth
// wiring that up, which lands in a follow-up pass.
//
// Contract:
//   data-controller="funnel-builder"
//   data-funnel-builder-funnel-id-value="123"
//   data-funnel-builder-selected-step-value="456"
//
// Targets:
//   - paletteItem  : draggable palette card
//   - canvas       : the central drop zone
//   - node         : a rendered FunnelStep node
//   - inspector    : the right pane
//   - saveChip     : the topbar "Enregistré" pill
export default class extends Controller {
  static targets = ["paletteItem", "canvas", "node", "inspector", "saveChip"]
  static values  = { funnelId: Number, selectedStep: Number }

  connect() {
    this.#wirePaletteDrag()
    this.#wireKeyboardSelection()
    this.#flashSaved("Prêt")
  }

  // Palette drag start — sets the page_type into the drag data so the
  // canvas drop handler knows what kind of step to create when wired.
  #wirePaletteDrag() {
    this.paletteItemTargets.forEach(card => {
      card.addEventListener("dragstart", (e) => {
        const type = card.dataset.pageType
        if (!type) return
        e.dataTransfer.setData("application/x-funnel-step-type", type)
        e.dataTransfer.effectAllowed = "copy"
        card.classList.add("is-dragging")
      })
      card.addEventListener("dragend", () => card.classList.remove("is-dragging"))
    })

    if (!this.hasCanvasTarget) return
    this.canvasTarget.addEventListener("dragover", (e) => {
      const types = Array.from(e.dataTransfer.types || [])
      if (types.includes("application/x-funnel-step-type")) {
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
      }
    })
    this.canvasTarget.addEventListener("drop", (e) => {
      const type = e.dataTransfer.getData("application/x-funnel-step-type")
      if (!type) return
      e.preventDefault()
      this.#createStep(type)
    })
  }

  // ← / → moves selection between nodes for keyboard users.
  #wireKeyboardSelection() {
    document.addEventListener("keydown", (e) => {
      if (!this.hasNodeTarget) return
      if (e.target.matches("input, textarea, select, [contenteditable]")) return
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return

      const ids = this.nodeTargets.map(n => Number(n.dataset.stepId))
      const current = this.selectedStepValue || ids[0]
      const idx = ids.indexOf(current)
      const next = e.key === "ArrowRight"
        ? ids[Math.min(idx + 1, ids.length - 1)]
        : ids[Math.max(idx - 1, 0)]
      if (next && next !== current) {
        const target = this.nodeTargets.find(n => Number(n.dataset.stepId) === next)
        const link = target?.querySelector("a.fn-node__link")
        link?.click()
      }
    })
  }

  // POST to FunnelStepsController#quick_create. The server response is a
  // 302 back to the builder with the new step appended — we let the
  // browser follow that redirect so the canvas re-renders with the new
  // node in the right slot. CSRF token is read from the meta tag Rails
  // sets in the layout head.
  async #createStep(pageType) {
    const funnelId = this.funnelIdValue
    if (!funnelId) return
    this.#flashSaved("Ajout…")
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const url  = `/admin/funnels/${funnelId}/funnel_steps/quick_create`
    try {
      const resp = await fetch(url, {
        method:  "POST",
        headers: {
          "X-CSRF-Token": csrf || "",
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept":       "text/html"
        },
        body:        new URLSearchParams({ page_type: pageType }).toString(),
        credentials: "same-origin",
        redirect:    "follow"
      })
      if (resp.redirected) {
        window.location.assign(resp.url)
      } else if (resp.ok) {
        window.location.reload()
      } else {
        this.#flashSaved("Échec ajout")
        console.warn(`[funnel-builder] quick_create returned ${resp.status}`)
      }
    } catch (e) {
      this.#flashSaved("Échec ajout")
      console.error("[funnel-builder] quick_create failed", e)
    }
  }

  // Brief surface for the "Enregistré" chip. We don't yet auto-save —
  // the chip just acknowledges visible activity for now.
  #flashSaved(label = "Enregistré") {
    if (!this.hasSaveChipTarget) return
    this.saveChipTarget.textContent = label
    this.saveChipTarget.classList.add("is-flashing")
    clearTimeout(this._flashTimer)
    this._flashTimer = setTimeout(() => {
      this.saveChipTarget.classList.remove("is-flashing")
    }, 800)
  }
}
